import { isDefined, isNotDefined } from '@sgme/fp';
import { Button, HStack, Spacer, VStack } from '@sgme/ui';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useAlgoTileId } from '@/App/tiles/orders/AlgoOrders/AlgoContext.tsx';
import AlgoActionButtons from '@/App/tiles/orders/AlgoOrders/AlgoOrderExecutedTile/AlgoActionButtons';
import BackButton from '@/App/tiles/orders/AlgoOrders/AlgoOrderExecutedTile/BackButton.tsx';
import { ExecutedActionButtons } from '@/App/tiles/orders/AlgoOrders/AlgoOrderExecutedTile/ExecutedActionButtons';
import { ExecutedTileField } from '@/App/tiles/orders/components/ExecutedTileField.tsx';
import { ProgressBar } from '@/App/tiles/orders/components/ProgressBar.tsx';
import { Summary } from '@/App/tiles/orders/components/Summary.tsx';
import { ExecutionPendingOrderStatus } from '@/App/tiles/orders/ExecutionPendingOrderStatus.tsx';
import { useAccount } from '@/App/tiles/orders/hooks.tsx';
import { getOrderWay, getStatusClass, isOrderEditable, mapStatusToColor } from '@/App/tiles/orders/utils.ts';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { checkIfUserCanTrade, useUserContext } from '@/context/UserContext.tsx';
import { finalStatuses } from '@/store/bulkOrder/state.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { selectTradeIdFromAlgoTile } from '@/store/orders/algoOrders/selectors.ts';
import { selectOrderEntryByIdFromBlotter } from '@/store/orders/fixing/selectors.ts';
import type { FixingPriceType } from '@/store/orders/fixing/state.ts';
import type { AlgoOrderType } from '@/store/orders/state.ts';
import { removeTile } from '@/store/tiles/slice.ts';
import { deepEqual } from '@/utils/object.ts';

type AlgoOrderExecutedTileProps = {
  algoType: AlgoOrderType;
};

export const AlgoOrderExecutedTile = (props: AlgoOrderExecutedTileProps) => {
  const { algoType } = props;
  const tileId = useAlgoTileId();
  const [showBottomAttributes, setShowBottomAttributes] = useState(false);

  const dispatch = useDispatch();
  const userContextValue = useUserContext();
  const readOnly = !checkIfUserCanTrade(userContextValue);

  const { formatMessage, formatDate, formatNumber } = useIntl();

  const tradeId = useAppSelector((state) => selectTradeIdFromAlgoTile(state, tileId), deepEqual);

  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );

  const account = useAccount(orderFromBlotter);

  const canEditOrder = isOrderEditable(orderFromBlotter?.status, tradeId);

  const isPaused = orderFromBlotter?.status === 'Paused';
  const isFinal = finalStatuses.includes(orderFromBlotter?.status ?? '');
  const color = mapStatusToColor(orderFromBlotter?.status);

  function handleClose() {
    dispatch(removeTile(tileId));
  }

  if (isNotDefined(orderFromBlotter)) {
    logger.logInformation(`[ORDER] Tile with id ${tileId} not found in blotter.`);
    return <ExecutionPendingOrderStatus />;
  }

  const way = getOrderWay(
    orderFromBlotter.currencyPair,
    orderFromBlotter?.notionalCurrency,
    orderFromBlotter?.yourWay ?? 'Buy',
  );

  return (
    <div className="d-flex w-100 h-100">
      <VStack className="bg-lvl1 py-2" fullWidth fullHeight>
        <HStack className="m-2">
          <div>
            <span data-e2e="order-tradeId" className="text-primary fw-bold me-4px">
              Order {tradeId}
            </span>
            <span data-e2e="order-status-text" className={getStatusClass(orderFromBlotter?.status)}>
              {orderFromBlotter?.status}
            </span>
          </div>
          <button type="button" className="btn btn-flat-primary" onClick={handleClose} data-e2e="close-tile">
            <em className="icon icon-sm">close</em>
          </button>
        </HStack>
        <div className={`bg-${color} bg-opacity-10 text-${color} p-3 my-3`}>
          <ProgressBar
            executedAmount={orderFromBlotter?.executedAmount}
            notional={orderFromBlotter?.notional}
            color={color}
            isFinal={isFinal}
          />

          <div className="my-3">
            <span className="summary-text">
              <Summary
                account={account}
                orderType={orderFromBlotter?.orderType ?? 'Call'}
                way={orderFromBlotter?.yourWay ?? null}
                isGtc={orderFromBlotter?.isGtc ?? false}
                notional={orderFromBlotter?.notional ?? null}
                currency={orderFromBlotter?.notionalCurrency ?? null}
                limitPrice={orderFromBlotter?.limitPrice ?? null}
                expiryDay={orderFromBlotter?.expiryDay ?? null}
                expiryTime={orderFromBlotter?.expiryTime ?? null}
                currencyPair={orderFromBlotter?.currencyPair ?? null}
                fixingPlaceOrTime={orderFromBlotter?.fixingTime ?? orderFromBlotter?.fixingPlace ?? null}
                fixingDate={orderFromBlotter?.fixingDateUtc ?? null}
                fixingRef={orderFromBlotter?.fixingBenchmark ?? null}
                priceType={(orderFromBlotter?.fixingPriceType as FixingPriceType) ?? null}
                finalStatus={isFinal}
              />
            </span>
          </div>
          <AlgoActionButtons isPaused={isPaused} tradeId={tradeId} canEditOrder={canEditOrder} algoType={algoType} />
        </div>
        <div>
          <ExecutedTileField label="currencyPair" value={orderFromBlotter?.currencyPair} />
          <ExecutedTileField label="orderType" value={orderFromBlotter?.orderType} />
          <ExecutedTileField
            label="way"
            value={`${formatMessage({ id: way === 'Buy' ? 'way.buy' : 'way.sell' })} ${orderFromBlotter.notionalCurrency}`}
          />
          <ExecutedTileField
            label="notional"
            value={`${isDefined(orderFromBlotter?.notional) ? formatNumber(Number(orderFromBlotter?.notional)) : ''} ${orderFromBlotter?.notionalCurrency}`}
          />
          <ExecutedTileField label="noWorseThan" value={orderFromBlotter?.limitPrice} />
          <ExecutedTileField label="averagePrice" value={orderFromBlotter?.averagePrice} isLast />

          {/* ------------------------------------------^----------------------------------- */}

          <HStack verticalAlignment="center" fullWidth className="px-2">
            <div className="flex-fill border-top" style={{ height: 1 }} />

            <Button
              size="sm"
              purpose="flat"
              onClick={() => setShowBottomAttributes(!showBottomAttributes)}
              data-e2e="other-attributes-toggle-button"
            >
              <FormattedMessage id="order.form.otherAttributes" />
              <i className="icon icon-sm">{!showBottomAttributes ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</i>
            </Button>

            <div className="flex-fill border-top" style={{ height: 1 }} />
          </HStack>

          {showBottomAttributes && (
            <>
              <ExecutedTileField
                label="tenor"
                value={
                  isDefined(orderFromBlotter?.maturityDate)
                    ? formatDate(orderFromBlotter?.maturityDate, { dateStyle: 'medium', timeStyle: 'short' })
                    : ''
                }
              />
              <ExecutedTileField
                label="startDate"
                value={
                  isDefined(orderFromBlotter?.startTime)
                    ? formatDate(orderFromBlotter?.startTime, { dateStyle: 'medium', timeStyle: 'short' })
                    : ''
                }
              />
              <ExecutedTileField
                label="expiryDate"
                value={
                  isDefined(orderFromBlotter?.endTime)
                    ? formatDate(orderFromBlotter?.endTime, { dateStyle: 'medium', timeStyle: 'short' })
                    : ''
                }
              />
              <ExecutedTileField label="liquidityPool" value={orderFromBlotter?.liquidityPool} />

              {algoType === 'Twap+Order' && (
                <>
                  <ExecutedTileField label="clippingMode" value={orderFromBlotter?.clippingMode} />
                  <ExecutedTileField label="clipSize" value={orderFromBlotter?.clipSize} />
                  <ExecutedTileField
                    label="randomize"
                    value={formatMessage({ id: orderFromBlotter?.randomize ? 'yes' : 'no' })}
                  />
                  <ExecutedTileField
                    isLast
                    label="spreadCapture"
                    value={formatMessage({ id: orderFromBlotter?.spreadCapture ? 'yes' : 'no' })}
                  />
                </>
              )}

              {algoType === 'NightjarOrder' && (
                <>
                  <ExecutedTileField label="speed" value={orderFromBlotter?.speed} />

                  <ExecutedTileField
                    label="alphaSeeker"
                    value={formatMessage({ id: orderFromBlotter?.alphaSeeker ? 'yes' : 'no' })}
                    isLast
                  />
                </>
              )}

              {algoType === 'FalconOrder' && <ExecutedTileField label="speed" value={orderFromBlotter?.speed} isLast />}
            </>
          )}
        </div>
        <Spacer />
        {canEditOrder ? (
          <ExecutedActionButtons tradeId={tradeId} algoType={algoType} currencyPair={orderFromBlotter?.currencyPair} />
        ) : (
          <HStack
            horizontalAlignment="end"
            verticalAlignment="center"
            margin="8px"
            gap="8px"
            data-e2e="action-buttons-container"
          >
            <BackButton currencyPair={orderFromBlotter?.currencyPair} readOnly={readOnly} />
          </HStack>
        )}
      </VStack>
    </div>
  );
};
